import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import FolderIcon from '@material-ui/icons/Folder';
import LaunchIcon from '@material-ui/icons/Launch';

import SelectMenu from 'components/Staffing/SelectMenu/SelectMenu';
import PillButton from 'components/PillButton';

import { useDispatch, useSelector } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import { selectCompany } from 'redux/selectors/staffing/getCompanies';
import { deleteCompany, openCreateCompanyDialog } from 'redux/actions/staffingActions';
import { getFirestoreDocumentUrl } from 'utils/getFirestoreDocumentUrl';
import { VC_HUBSPOT_ACCOUNT_ID } from 'constants/staffing';

import './companyCard.scss';

/** Matches ControlRoomUserRoles.SUPER_ADMIN on the service (user doc + auth claims). */
const SUPER_ADMIN_ROLE = 'superAdmin';

/**
 * Super-admin tooling (Firestore console links) is not for every staffing admin. Check the
 * Firestore user `roles` array first; fall back to auth custom claims because
 * react-redux-firebase `enableClaims` mirrors them on `profile.token.claims`.
 *
 * @param {{ roles?: string[], token?: { claims?: { roles?: string[] } } }|null|undefined} profile
 * @returns {boolean}
 */
const isSuperAdmin = profile => {
  const docRoles = profile?.roles;
  if (Array.isArray(docRoles) && docRoles.includes(SUPER_ADMIN_ROLE)) {
    return true;
  }

  const claimRoles = profile?.token?.claims?.roles;
  return Array.isArray(claimRoles) && claimRoles.includes(SUPER_ADMIN_ROLE);
};

/**
 * Staffing list card for a single company. Edit and delete are exposed from the overflow menu;
 * delete is disabled while the company still has deals (API also 409s as a backstop).
 * Clicking the header opens the same edit dialog as the menu. Super admins also get a deep link
 * into the companies document for the current environment. Drive folder and HubSpot pills render
 * only when those records exist on the company doc.
 *
 * @param {{ id: string }} props
 */
const CompanyCard = props => {
  const { id } = props;
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const getCompany = selectCompany();
  const company = useSelector(state => getCompany(state, id));
  const user = useSelector(state => state.firebase.profile);

  const openEdit = () => {
    dispatch(openCreateCompanyDialog(true, id));
  };

  if (!company) {
    return null;
  }

  const dealLabel = company.dealCount === 1 ? '1 deal' : `${company.dealCount} deals`;
  const hasAssociatedDeals = company.dealCount > 0;

  /**
   * Confirm, then DELETE /v1/companies/:id. The menu item is disabled when dealCount > 0 so
   * staff see why before they click; the API still blocks a race if a deal lands in between.
   */
  const handleDelete = () => {
    confirm({
      description: `You are about to delete ${company.companyName}. This action is permanent.`,
    }).then(() => {
      dispatch(deleteCompany(id));
    });
  };

  // Company Drive folders are created on demand (projects on company create, contracts/P&L
  // during deal init, transcripts when routing unmatched recordings). Only link when stored.
  // Prefer the renamed transcripts field; fall back to the legacy orphaned-folder field.
  const transcriptsFolder =
    company.companyMeetingTranscriptsFolder || company.meetingTranscriptsOrphanedFolder;
  const driveFolderPills = [
    { folder: company.clientProjectsFolder, text: 'Projects' },
    { folder: company.clientContractsFolder, text: 'Contracts' },
    { folder: company.clientPLFolder, text: 'P&L' },
    { folder: transcriptsFolder, text: 'Transcripts' },
  ].filter(item => item.folder?.webViewLink);

  // HubSpot company records live at /record/0-2/{id} (object type 0-2). Do not
  // mirror Deal Detail's legacy /deal/{id} path — that is deals (0-3), not companies.
  const hubspotCompanyUrl = company.hubspotCompanyId
    ? `https://app.hubspot.com/contacts/${VC_HUBSPOT_ACCOUNT_ID}/record/0-2/${company.hubspotCompanyId}`
    : null;

  const menuOptions = [
    {
      title: 'Edit',
      onItemClick: openEdit,
    },
    {
      title: 'Delete',
      disabled: hasAssociatedDeals,
      tooltip: hasAssociatedDeals ? 'Companies with associated deals cannot be deleted' : undefined,
      onItemClick: handleDelete,
    },
  ];

  // Same deep-link helper as Project detail: emulator UI locally, Firebase Console in
  // deployed env. Hidden unless the signed-in user is a super admin.
  if (isSuperAdmin(user)) {
    menuOptions.push({
      title: 'View in Firestore',
      onItemClick: () => {
        window.open(getFirestoreDocumentUrl('companies', id), '_blank', 'noopener,noreferrer');
      },
    });
  }

  return (
    <Card className={cx('company-card')}>
      <div className="company-card-header">
        <div onClick={openEdit}>
          <Typography className="company-card-name">{company.companyName}</Typography>
          <Typography className="company-card-domain">{company.domain || 'No domain'}</Typography>
        </div>

        <SelectMenu
          options={menuOptions}
          menuContainerClassName="company-card-menu"
          menuBtnClassName="company-card-menu-button"
        />
      </div>

      <div className="company-card-separator" />

      <Typography className="company-card-body-description">{dealLabel}</Typography>

      {(driveFolderPills.length > 0 || hubspotCompanyUrl) && (
        <div className="company-card-pills">
          {driveFolderPills.map(item => (
            <PillButton
              key={item.text}
              type="externalUrl"
              externalUrl={item.folder.webViewLink}
              text={item.text}
              Icon={FolderIcon}
            />
          ))}
          {hubspotCompanyUrl && (
            <PillButton
              type="externalUrl"
              externalUrl={hubspotCompanyUrl}
              text="Hubspot"
              Icon={LaunchIcon}
            />
          )}
        </div>
      )}
    </Card>
  );
};

CompanyCard.propTypes = {
  id: PropTypes.string.isRequired,
};

export default CompanyCard;
